import { KeyboardEvent } from 'react';
import clsx from 'clsx';

import { inputStyles, sizeStyle } from '@components/input/input.config';
import useCurrency from '@hooks/useCurrency';
import { sanitizeNumberInput } from '@utils/generic';
import { AmountInputProps } from 'types/input';

interface TermAmountInputProps extends AmountInputProps {
  tooltipId: string;
  isFeeCategoryEnabled?: boolean;
  isFieldDisabled?: boolean;
}

const TermAmountInput = (props: TermAmountInputProps) => {
  const {
    className,
    name,
    type = 'text',
    inputClassNames,
    inputSize = 'medium',
    value,
    disabled,
    placeholder = '0',
    onAmountChange,
    maxAmount,
    error,
    handleInputBlur,
    allowZero,
    onClick,
    allowDecimals = false,
    tooltipId,
    isFeeCategoryEnabled,
    isFieldDisabled,
    ...rest
  } = props;

  const handleOnClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const { formatCurrencyValue } = useCurrency();

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    // Prevent the default behavior for arrow keys
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    // Prevent value incrementing / decrementing on scroll
    e.preventDefault();
    e.currentTarget.blur();
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();

    // Determine allowed pattern based on the allowDecimals prop
    const regex = allowDecimals ? /^[0-9]*\.?[0-9]{0,2}$/ : /^[0-9]*$/;

    const inputValue = event.target.value;

    if (!regex.test(inputValue)) {
      return;
    }

    const amount = sanitizeNumberInput(inputValue, allowDecimals);

    const integerPart = amount.split('.')[0] || '';
    if (integerPart.length > 10) return;

    let formattedAmount = amount;

    if (
      !allowZero &&
      amount !== '' &&
      (Number(formattedAmount) <= 0 ||
        Number(formattedAmount) > Number(maxAmount))
    ) {
      formattedAmount = '';
      return;
    }

    formattedAmount = amount;
    onAmountChange(formattedAmount);
  };

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (handleInputBlur) {
      const inputValue = e.target.value.trim();
      handleInputBlur(inputValue);
    }
  };

  return (
    <div
      className={`${
        inputStyles.common
      } relative flex w-full items-center rounded-lg px-4 py-0 ${
        error
          ? `${inputStyles.error} min-w-60`
          : 'hover:focus-within:border-primary'
      } ${sizeStyle[inputSize]} ${className}`}
      onKeyDown={() => null}
      role="presentation"
      data-tooltip-id={`${tooltipId}_term-due-amount`}
    >
      <div
        className={`flex h-full items-center justify-center !font-inter ${
          isFieldDisabled ? 'opacity-50' : ''
        }`}
      >
        {formatCurrencyValue(Number(value)).symbol}
      </div>
      <div className="h-4 border border-transparent border-r-cofeeNeutralInactive pr-3.5" />
      <input
        id={name}
        name={name}
        type={type}
        className={clsx(
          inputClassNames,
          inputStyles,
          'no-spinners min-w-0 flex-1 bg-transparent pl-3 focus:outline-none'
        )}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
        onClick={handleOnClick}
        onChange={handleInputChange}
        onBlur={onInputBlur}
        {...rest}
      />
      {isFeeCategoryEnabled && (
        <div
          role="presentation"
          onClick={isFieldDisabled ? () => {} : onClick || handleOnClick}
          className={`flex h-full items-center justify-center ${
            isFieldDisabled ? 'cursor-not-allowed' : 'cursor-pointer'
          }`}
        >
          <div className="mr-2 h-12 border border-transparent border-r-cofeeNeutralInactive" />
          <p
            className={`text-nowrap text-sm text-primary ${
              isFieldDisabled ? 'bg-transparent opacity-40' : ''
            }`}
          >
            Edit Fee
          </p>
        </div>
      )}
    </div>
  );
};

export default TermAmountInput;
